import { useState, useEffect } from 'react';
import type { Socket } from 'socket.io-client';
import type { ChatMessage } from '../types';
import type { SignalingService } from '@/features/videocall/services/signaling.service';

interface UseChatProps {
  roomId: string;
  isHost: boolean;
  socketRef: React.MutableRefObject<Socket | null>;
  signalingServiceRef: React.MutableRefObject<SignalingService | null>;
  activePanel: 'chat' | 'notes' | 'annotate' | 'measure' | null;
  displayName?: string;
}

export function useChat({
  roomId,
  isHost,
  socketRef,
  signalingServiceRef,
  activePanel,
  displayName,
}: UseChatProps) {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  // ✅ Use SignalingService for chat messages
  useEffect(() => {
    const signalingService = signalingServiceRef.current;
    if (!signalingService) {
      console.log('⏳ SignalingService not ready for chat, waiting...');
      return;
    }

    console.log('🎧 Setting up chat message listener via SignalingService');

    const cleanup = signalingService.onChatMessage((incoming: any) => {
      const msg = incoming?.message ?? incoming;
      const rawTs = msg?.timestamp;
      const parsedTs = rawTs ? new Date(rawTs) : new Date();
      const normalized: ChatMessage = {
        id: String(msg?.id ?? Date.now()),
        text: String(msg?.text ?? ""),
        sender: String(msg?.sender ?? "User"),
        timestamp: parsedTs,
        isHost: !!msg?.isHost,
      };

      console.log('💬 Received chat message via SignalingService:', {
        text: normalized.text,
        sender: normalized.sender,
        senderId: msg?.senderId,
      });

      setChatMessages(prev => (
        prev.some(m => m.id === normalized.id) ? prev : [...prev, normalized]
      ));

      // Check if message is from peer (not from self)
      const socket = socketRef.current;
      const fromPeer = msg?.senderId ? msg.senderId !== socket?.id : true;
      if (activePanel !== 'chat' && fromPeer) {
        setHasUnreadMessages(true);
      }
    });

    return cleanup;
  }, [signalingServiceRef, socketRef, activePanel]);

  const sendMessage = () => {
    const text = newMessage.trim();
    if (!text) return;

    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const message: ChatMessage = {
      id: uid,
      text,
      sender: displayName || (isHost ? 'Host' : 'Guest'),
      timestamp: new Date(),
      isHost: isHost,
    };

    // ✅ Add message locally first (optimistic update)
    setChatMessages(prev => [...prev, message]);
    setNewMessage('');

    // ✅ Use SignalingService to send message
    const signalingService = signalingServiceRef.current;
    if (signalingService) {
      console.log('📤 Sending chat message via SignalingService:', text.substring(0, 50));
      signalingService.sendChatMessage({
        id: uid,
        text,
        sender: displayName || (isHost ? 'Host' : 'Guest'),
        timestamp: message.timestamp.toISOString(),
        isHost: isHost,
      });
    } else {
      console.error('❌ SignalingService not available, cannot send chat message');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  return {
    chatMessages,
    newMessage,
    hasUnreadMessages,
    setNewMessage,
    setHasUnreadMessages,
    sendMessage,
    handleKeyPress,
  };
}